import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMeusReembolsos } from '../../../hooks/useMeusReembolsos';
import { ContadoresStatus, PedidoReembolsoCard } from '../../../components/financeiro';
import type { ContagemStatus } from '../../../components/financeiro';
import type { PedidoReembolso } from '../../../types/pedidoReembolso';

// Aba Reembolsos do hub financeiro do atleta (financeiro-dashboard-atleta Phase 5, Task 5 do
// plano; AC-A1, AC-A2). Contadores por status derivados da lista carregada — cardinalidade, não
// valor monetário, não conflita com "zero agregação no cliente" (AC-7); meusPedidosReembolso não
// tem filtro de status no contrato e a AC-10 pede que os contadores "batam com a lista". Cada
// pedido reusa o card existente (agora em components/financeiro/, Task 3), com as flags de papel
// como false e os callbacks de decisão como no-op — o atleta não aprova/recusa/executa/finaliza;
// essas ações continuam em PedidosReembolsoPage (organizador/admin). "Ver detalhe" navega para
// /athlete/payments/:pagamentoId/refunds, fora do card (a interface de props do card não muda).
// Estados obrigatórios herdados de AC-12: erro com "Tentar novamente" (refetch), vazio específico.

function contarPorStatus(pedidos: PedidoReembolso[]): ContagemStatus[] {
  const contagens = new Map<string, number>();
  pedidos.forEach((pedido) => {
    contagens.set(pedido.status, (contagens.get(pedido.status) ?? 0) + 1);
  });
  return Array.from(contagens.entries()).map(([status, total]) => ({ status, total }));
}

const noop = () => {};

const ReembolsosTab: React.FC = () => {
  const navigate = useNavigate();
  const { pedidos, loading, error, refetch } = useMeusReembolsos();

  // AC-12: falha de rede — mensagem amigável + ação de repetir, sem crash e sem catch silencioso.
  if (error) {
    return (
      <div className="text-center py-5" data-testid="reembolsos-erro">
        <p className="text-danger mb-3">Não foi possível carregar os reembolsos. Tente novamente.</p>
        <button type="button" className="btn btn-primary" onClick={() => refetch()}>
          Tentar novamente
        </button>
      </div>
    );
  }

  // Vazio específico — o atleta ainda não solicitou nenhum reembolso.
  if (!loading && pedidos.length === 0) {
    return (
      <div className="text-center py-5" data-testid="reembolsos-vazio">
        <p className="mb-0">Você ainda não solicitou nenhum reembolso.</p>
      </div>
    );
  }

  return (
    <div>
      <ContadoresStatus contagens={contarPorStatus(pedidos)} />
      {pedidos.map((pedido) => (
        <div key={pedido.id}>
          <PedidoReembolsoCard
            pedido={pedido}
            isOrganizador={false}
            isAdmin={false}
            canExecute={false}
            executeDisabledReason={null}
            onAprovar={noop}
            onRecusar={noop}
            onExecutar={noop}
            onFinalizar={noop}
            onHistorico={noop}
          />
          <div className="text-end mb-3">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => navigate(`/athlete/payments/${pedido.pagamentoInscricao?.id}/refunds`)}
            >
              Ver detalhe
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ReembolsosTab;
